import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { AlertTriangle, CalendarClock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Tasks() {
  const { t } = useI18n();
  const [tasks, setTasks] = useState([]);
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("open");
  const [form, setForm] = useState({
    title: "",
    due_date: new Date().toISOString().slice(0, 10),
    student_id: "none",
    assigned_to: "self",
  });

  const load = async () => {
    const q = {};
    if (filter === "open") q.done = false;
    if (filter === "done") q.done = true;
    const [tk, ss, us] = await Promise.all([
      api.get("/tasks", { params: q }),
      api.get("/students"),
      api.get("/users"),
    ]);
    setTasks(tk.data); setStudents(ss.data); setUsers(us.data);
  };
  useEffect(() => { load().catch(() => {}); }, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      await api.post("/tasks", {
        title: form.title,
        due_date: form.due_date,
        student_id: form.student_id === "none" ? null : form.student_id,
        assigned_to: form.assigned_to === "self" ? null : form.assigned_to,
      });
      setForm({ ...form, title: "" });
      toast.success(t("addTask"));
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggle = async (task) => {
    await api.patch(`/tasks/${task.id}`, { done: !task.done });
    await load();
  };

  const today = new Date().toISOString().slice(0, 10);
  const studentName = (id) => students.find((s) => s.id === id)?.name || "";
  const userName = (id) => users.find((u) => u.id === id)?.name || t("unassigned");

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1200px] mx-auto">
        <div className="mb-6">
          <div className="eyebrow">{t("appName")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0B192C] mt-1">
            {t("tasks")}
          </h1>
        </div>

        <Card className="p-5 border border-slate-200 shadow-none mb-6">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end" data-testid="task-form">
            <div className="md:col-span-2">
              <Label className="text-xs">{t("title")}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder={t("addTask")} data-testid="task-title" />
            </div>
            <div>
              <Label className="text-xs">{t("dueDate")}</Label>
              <Input type="date" value={form.due_date}
                     onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                     data-testid="task-due" />
            </div>
            <div>
              <Label className="text-xs">{t("student")}</Label>
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                <SelectTrigger data-testid="task-student"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="bg-[#7A1B28] hover:bg-[#601520] text-white"
                    data-testid="task-save">{t("save")}</Button>
          </form>
        </Card>

        <div className="flex items-center gap-2 mb-3">
          {["open", "done", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
                    className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-md border ${
                      filter === f
                        ? "bg-[#0B192C] text-white border-[#0B192C]"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid={`filter-${f}`}>
              {f === "open" ? t("open") : f === "done" ? t("completed") : t("all")}
            </button>
          ))}
        </div>

        <Card className="border border-slate-200 shadow-none divide-y divide-slate-100" data-testid="tasks-list">
          {tasks.length === 0 && <div className="p-6 text-sm text-slate-500">{t("empty")}</div>}
          {tasks.map((tk) => {
            const overdue = !tk.done && tk.due_date < today;
            const isToday = tk.due_date === today && !tk.done;
            return (
              <div key={tk.id} className="p-4 flex items-center gap-3 hover:bg-slate-50">
                <input type="checkbox" checked={tk.done} onChange={() => toggle(tk)}
                       data-testid={`task-toggle-${tk.id}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${tk.done ? "line-through text-slate-400" : "text-slate-900 font-medium"}`}>
                    {tk.title}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="flex items-center gap-1"><CalendarClock size={12} />{tk.due_date}</span>
                    {tk.student_id && (
                      <Link to={`/students/${tk.student_id}`} className="text-[#7A1B28] hover:underline">
                        {studentName(tk.student_id)}
                      </Link>
                    )}
                    <span>· {userName(tk.assigned_to)}</span>
                  </div>
                </div>
                {overdue && (
                  <span className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-200 inline-flex items-center gap-1">
                    <AlertTriangle size={12} /> {t("overdue")}
                  </span>
                )}
                {isToday && (
                  <span className="text-xs px-2 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
                    {t("dueToday")}
                  </span>
                )}
                {tk.done && (
                  <span className="text-xs text-emerald-600 inline-flex items-center gap-1">
                    <CheckCircle2 size={14} /> {t("completed")}
                  </span>
                )}
              </div>
            );
          })}
        </Card>
      </div>
    </Layout>
  );
}
